'use client';

import FloorPlanViewer from '../FloorPlanViewer';
import RulerIcon from '../Icons/Ruler';
import RoomsEnum from '../RoomsEnum';

import {VillageFloor, VillageRooms} from '@/services/types';
import {IVillageDictionary} from '@/dictionaries/pages/village/types';
import translateFromArray from '@/utils/get-translated-object';

type Props = {
  villageDict: IVillageDictionary;
  rooms: VillageRooms[];
  villageId: number;
  lang: string;
};

export default function RoomsContent({
  rooms,
  villageId,
  lang,
  villageDict,
}: Props) {
  return rooms.map(({translations, surface, imageCount, floors, id, type}) => {
    const roomsEnum = villageDict.rooms.enums;

    const titleCheck =
      translations.length > 0
        ? translateFromArray({lang, translations})?.title
        : 'Main house';

    const roomTitle = type === 'custom' ? titleCheck : roomsEnum[type];

    const hasFloorPlan = imageCount > 0 && type !== 'other_areas';

    const roomFloors: VillageFloor[] = [];

    Object.values(floors).forEach((floor) => roomFloors.push(floor));

    return (
      <div
        className="border-b-2 border-gray-200 mb-8 last:border-b-0 md:flex md:flex-wrap"
        key={id}>
        <div className="w-full md:w-[33.33333%] flex-[0_0_auto]">
          <div className="bg-gray-100 border border-gray-200 pt-[20px] px-[20px] w-full md:w-[90%]">
            <h3 className="text-3xl font-title mb-2 font-semibold">
              {roomTitle}
            </h3>
            <ul className="ml-0 list-none flex flex-col gap-2 mb-4">
              <li className="text-gray-700 flex items-center">
                <RulerIcon className="fill-current w-6 h-6 mr-2" />
                {surface} MQ
              </li>
              {hasFloorPlan && (
                <FloorPlanViewer.Canvas
                  lang={lang}
                  houseId={id}
                  villageId={villageId}
                  seeFloorPlanLabel={villageDict.rooms.seeFloorPlan}
                />
              )}
            </ul>
          </div>
        </div>
        <div></div>
        <div className="w-full md:w-[66.66667%] flex-[0_0_auto] flex flex-wrap mx-[-0.625rem] md:mx-[-0.9375rem]">
          {roomFloors.map(({translations, rooms}, index) => (
            <div className="w-full flex flex-wrap flex-[0_0_auto]" key={index}>
              <div className="w-[calc(100%-1.25rem)] flex-[0_0_auto] md:w-[calc(100%-1.875rem)] mx-[0.625rem] md:mx-[0.9375rem]">
                <h4 className="mb-8 text-medium font-semibold mt-6 font-title">
                  {translations[lang]?.title}
                </h4>
              </div>
              {rooms.map(
                ({title, roomType, roomFeatures, translations}, index) => {
                  const formatedRoomFeatures: string[] = [];
                  Object.values(roomFeatures).forEach((feature) => {
                    if (roomType === 'custom') {
                      formatedRoomFeatures.push(feature);
                    } else {
                      formatedRoomFeatures.push(feature?.[0]);
                    }
                  });

                  return (
                    <div
                      key={index}
                      className="w-[calc(100%-1.25rem)] md:w-[calc(100%-1.875rem)] desktop:w-[calc(50%-1.875rem)] mx-[0.625rem] md:mx-[0.9375rem] mb-8 flex flex-col">
                      {roomType === 'custom' ? (
                        <h5 className="mr-8 font-semibold mb-2 text-body">
                          {translateFromArray({lang, translations})?.title}
                        </h5>
                      ) : (
                        <RoomsEnum.RoomType enumString={title} lang={lang} />
                      )}

                      <p className="mb-2 desktop:mr-8">
                        {translateFromArray({lang, translations})?.description}
                      </p>

                      {formatedRoomFeatures.map((feature, index) => {
                        return roomType == 'custom' ? (
                          <p key={index} className="mr-8 mb-2 leading-[1.6]">
                            {feature}
                          </p>
                        ) : (
                          <RoomsEnum.BedAndBathroomType
                            key={index}
                            enumString={feature}
                            lang={lang}
                          />
                        );
                      })}
                    </div>
                  );
                },
              )}
            </div>
          ))}
        </div>
      </div>
    );
  });
}
